import { useCallback } from "react";

import { useSelector } from "react-redux";

import {
  useRequestWithFeedback,
  parseErrorResponse,
  combineRunProps,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useLibrary } from "../../../providers";
import { selectUser } from "../../entities/users/selectors";

import { getLoanPaymentRequest } from "./utils";

import type { OverrideRunProps, AnyException } from "../../../composites";
import type { LoanPaymentFormValues } from "./types";
import type { RootState } from "../../entities/store";

export const useSubmitLoanPayment = (idempotencyKey?: string) => {
  const { send, loading } = useRequestWithFeedback<any, AnyException>();
  const { throwToast } = useLibrary("toasts");
  const user = useSelector((state: RootState) => selectUser(state));

  const submitLoanPayment = useCallback(
    (
      formValues: LoanPaymentFormValues,
      overrideRunProps: OverrideRunProps<any, AnyException> = {},
    ) => {
      if (!user?.id) {
        return;
      }
      send({
        action: getLoanPaymentRequest(idempotencyKey, formValues, user.id),
        ...combineRunProps<any, AnyException>(
          {
            onError: async (err) => {
              const { errors } = await parseErrorResponse(err);
              const defaultError =
                "We could not process your loan payment. Please try again or contact support.";
              const message = errors.length
                ? `${errors[0].description}`
                : defaultError;

              throwToast({
                kind: "error",
                message,
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, user?.id, idempotencyKey, throwToast],
  );

  return useObjectMemo({ submitLoanPayment, loading });
};
